/* Form Component to Add or Edit Evaluation Level 1 details*/

// IMPORT NECESSARY HOOKS AND COMPONENTS FROM REACT AND ANTD
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button,
  Col,
  Collapse,
  Descriptions,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Spin,
  Tag,
  Typography,
  Upload,
  Modal,
  message,
} from "antd";
import {
  BookOutlined,
  CheckCircleFilled,
  CheckOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import type { UploadFile, RcFile } from "antd/es/upload/interface";
import dayjs from "dayjs";
// IMPORT CUSTOM TYPES AND ENUMS
import {
  TContext,
  TDropdownOptions,
  TResponseModel,
} from "../../../types/config";
import {
  eEVAL_L1_IMAGES,
  eEVAL_L1_WORKFLOW,
  eHTTPStatusCode,
  eOUTPUT_STATUS,
  eOVERALLISTSTATUS,
  eResultCode,
} from "../../../utils/enum";
// IMPORT CUSTOM HOOKS AND UTILITY FUNCTIONS
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { TProduct, TScanResult } from "../../../types";
import {
  AddEditL1Evaluation,
  GetCustomerList,
  GetDefectLogBook,
  GetFaultCategoryList,
  GetProductList,
  GetScannedProductDetails,
  GetSpecificLeve1EvalDetails,
  GetStatorUsedList,
  UploadDocument,
  ValidateReEditPassword,
} from "../../../utils/api.constant";
import { UserContext } from "../../../context";
import "../../../index.css";
import styles from "./eval.module.css";
// IMPORT CUSTOM COMPONENTS
import { CloseCircleFilled, CloseOutlined } from "@ant-design/icons";
import FormLayout from "../../../components/form/form";
import DateUtils from "../../../utils/date";
import InfoDisplayLog from "../../../components/info-display-log/InfoDisplayLog";
import ScanResultCard from "../../../components/scan-result-card/ScanResultCard";
import Utils from "../../../utils";
import CustomModal from "../../../components/modal";
import AuthUtil from "../../../utils/auth";
import { UNAUTHORIZEDREQUEST } from "../../../utils/constants";
import React from "react";
import EncryptUtils from "../../../utils/encrypt";

// DESTRUCTURE COMPONENTS FROM ANTD
const { Title } = Typography;
// ENUM FOR LEVEL 1 OUTPUT MESSAGES
export enum eL1OUTPUTMSGS {
  NOTWARRANTYNOTCLAIMED = "Unit not claimed and Not In Warranty Verify Claim and Warranty",
  CLAIMEDANDINWARRANTY = "Unit Claimed and In Warranty - proceed to Evaluate",
  HIPOTFAIL = "Open junction connector box to verify for rust or damaged wiring",
  RUSTINCONNECTORYES = "Add images of rust and move unit to teardown",
  RUSTINCONNECTORNO = "Move the unit to Autospy Evaluation (Eval - L2)",
  NOOFTIMESSTATORUSED = "Refer to Defect Code Book for Disposition. Add images of found defect",
  UNITMOVETOTEARDOWN = "Unit can be moved to teardown",
  OLDERTHANYEAROLD = "As unit is more than a year old, move the unit to Tear down.",
  LESSTHANYEAROLD = "As unit is less than a year old, move the unit to Test Stand.",
  TESTSTANDNOFAULT = "Move unit to cage to ship back to Bloom every quarter",
  AUTOPSYEVALUTION = "Autopsy Evalution",
  PERFORMANCETESTING = "Performance Testing Started",
  DISPOSITION_PHASE = "Disposition Phase Started",
  CARBONRESIDUEYES = "Open Volute to verify Carbon Residue",
  NOCUTINUNIT = "Add Images of Carbon Residue",
  READYTOTEARDOWN = "Unit Ready for Teardown",
}

// ENUM FOR SCAN RESULTS
export enum eSCANRESULT {
  NOTWARRANTYNOTCLAIMED = 1,
  CLAIMEDANDINWARRANTY = 2,
}

export enum eHIPOT {
  PASS = 1,
  FAIL = 2,
}

export enum eRUSTINCONNECTOR {
  YES = 1,
  NO = 2,
}

export enum eNOOFTIMESSTATORUSED {
  ONE = 1,
  TWO = 2,
  THREE = 3,
  MORE_THAN_THREE = 4,
}

// ENUM FOR PERFORMANCE TESTING STATUS
export enum ePERFORMANCETESTING {
  // PROBLEM_FOUND = 1,
  // NO_PROBLEM_FOUND = 2,
  FAULT_FOUND = 1,
  NO_FAULT_FOUND = 2,
}

export enum eTESTSTANDRESULT {
  FAULT_FOUND = 1,
  NO_FAULT_FOUND = 2,
}

// ENUM FOR SIGN OF CARBON
export enum eSIGNOFCARBON {
  YES = 1,
  NO = 2,
}

export enum eVERIFYCUTINUNIT {
  YES = 1,
  NO = 2,
}
export enum eCARBONRESIDUEVERIFICATION {
  YES = 1,
  NO = 2,
}

// Eavl Level 1 Stage enum
export enum eL1_STAGES {
  EVAL_L1_SCAN = 1,
  HI_POT = 2,
  VISUAL_INSPECTION = 5,
  PERFORMANCE_TESTING = 3,
  DISPOSITION = 4,
}

// Eavl Level 1 Stage Name
export const eL1_STAGES_NAME: Record<eL1_STAGES, string> = {
  [eL1_STAGES.EVAL_L1_SCAN]: "Eval - L1 Scan",
  [eL1_STAGES.HI_POT]: "Hi - Pot",
  [eL1_STAGES.VISUAL_INSPECTION]: "Visual Inspection",
  [eL1_STAGES.PERFORMANCE_TESTING]: "Performance Testing",
  [eL1_STAGES.DISPOSITION]: "Disposition",
};

// TYPE DEFINITION FOR A LOG ENTRY
type TLogEntry = {
  message: string;
  timeStamp: Date;
  fieldName?: string;
  scanResult?: TScanResult;
  stageName?: string;
};

// TYPE DEFINITION FOR LEVEL 1 EVALUATION DATA
type TL1S1 = {
  dateOfEvaluation: string;
  typeOfUnit: number | string;
  modelNoOfUnit: number | string;
  unitSerialNo: string;
  scanResult: eSCANRESULT;
  hiPotStatus: eHIPOT;
  rustInConnectorStatus: eRUSTINCONNECTOR;
  noOfStatorUsedId: eNOOFTIMESSTATORUSED;
  performanceTestingStatusStatus: ePERFORMANCETESTING;
  testStandStatus: eTESTSTANDRESULT;
  faultCategoryId: number;
  logs: TLogEntry[];
};

// TYPE DEFINITION FOR COMPONENT PROPS
type TProps = {
  id?: number;
  submissionStatus?: eEVAL_L1_WORKFLOW;
  serialNumber?: string;
  productId?: number;
  customerId?: string | number;
  onCloseDrawer?: () => void;
  onRefreshList?: () => void;
  currentStage?: number;
  startEvalL1?: boolean;
};

export default function Level1Form(props: TProps) {
  const [form] = Form.useForm();

  // WATCH FOR CHANGES IN FORM FIELDS
  const typeOfUnit = Form.useWatch("typeOfUnit", form);
  const modelNoOfUnit = Form.useWatch("modelNoOfUnit", form);
  const hiPotStatus = Form.useWatch("hiPotStatus", form);
  const rustImgDescription = Form.useWatch("rustImgDescription", form);
  const rustInConnectorStatus = Form.useWatch("rustInConnectorStatus", form);
  const noOfStatorUsedId = Form.useWatch("noOfStatorUsedId", form);
  const performanceTestingStatus = Form.useWatch(
    "performanceTestingStatus",
    form
  );
  const visualImgDescription = Form.useWatch("visualImgDescription", form);
  const testStandStatus = Form.useWatch("testStandStatus", form);
  const faultCategoryId = Form.useWatch("faultCategoryId", form);
  const signOfCarbonResidue = Form.useWatch("signOfCarbonResidue", form);
  const carbonResidueVerification = Form.useWatch(
    "carbonResidueVerification",
    form
  );
  const cutInUnitStatus = Form.useWatch("cutInUnitStatus", form);
  const customer = Form.useWatch("customer", form);

  // to manage the uploaded images
  const [rustInConnectorImages, setRustInConnectorImages] = useState<
    UploadFile[]
  >([]);
  const [defectImages, setDefectImages] = useState<UploadFile[]>([]);
  const [carbonResidueImages, setCarbonResidueImages] = useState<UploadFile[]>(
    []
  );

  // STATE FOR MANAGING LOGS OR MESSAGE HISTORY
  const [logs, setLogs] = useState<TLogEntry[]>([]);

  // will have the current record eval record id
  const [l1FormId, setL1FormId] = useState<number>();
  const submissionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // STATE FOR MANAGING ACCORDION STAGES
  const [stage, setStage] = useState<number>(eL1_STAGES.EVAL_L1_SCAN);
  const [activeKey, setActiveKey] = useState<number | number[]>(
    eL1_STAGES.EVAL_L1_SCAN
  );

  // GET USER CONTEXT
  const context: TContext = useContext(UserContext);

  // STATE FOR DROPDOWN OPTIONS
  const [unitOptions, setUnitOptions] = useState<TDropdownOptions[]>([]);
  const [modelOptions, setModelOptions] = useState<TDropdownOptions[]>([]);
  const [productOptions, setProductOptions] = useState<TDropdownOptions[]>([]);
  const [faultCategoryOptions, setFaultCategoryOptions] = useState<
    TDropdownOptions[]
  >([]);
  const [statorUsedOption, setStatorOptions] = useState<TDropdownOptions[]>([]);
  const [customerOptions, setCustomerOptions] = useState<TDropdownOptions[]>(
    []
  );

  // CUSTOM HOOKS FOR API CALLS AND TOASTS
  const { post } = useFetch();
  const { onShowToast } = useToast();

  // STATE FOR MANAGING SCAN RESULTS
  const [scanResult, setScanResult] = useState<TScanResult | null>(null);
  const [inputSerialNo, setInputSerialNo] = useState<string>("");
  const [evalDate, setEvalDate] = useState<any>("");

  // TODO :: REMOVE THIS VARIABLE AS NOT GETTING USED ANYWHERE
  const [error, setError] = useState<string | null>(null);

  // STATE FOR TOGGLING USER LOGS PREFERENCE
  const [showLogs, setShowLogs] = useState<boolean>(() => {
    const saved = localStorage.getItem("showSystemLogs");
    return saved !== null ? JSON.parse(saved) : true;
  });

  // STATE FOR MANAGING MODAL STATUS AND MESSAGES
  const [outputMsgModal, showOuputMsgModal] = useState<boolean>(false);
  const [outputMessage, setOuputMessage] = useState<string>("");

  // STATE FOR MANAGING THE ALREADY PROCESSED SERIAL NUMBERS
  const [alreadyProcessedModal, showAlreadyProecessedModal] =
    useState<boolean>(false);
  const [alreadyProcessMsg, setAlreadyProcessedMsg] = useState<string>("");

  // STATE FOR CONFIRMATION MODALS
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [showOldSerialModal, setShowOldSerialModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  // STATE FOR IMAGE PREVIEW
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");

  // STATE FOR THE CURRENT STATUS OF THE EVALUATION RECORD
  const [currentStatus, setCurrentStatus] = useState<number>(
    props?.currentStage || 0
  );
  const [defectLogBookPath, setDefectLogBookPath] = useState<string>("");

  // INITIALLY SUBMISSION_STATUS IS FROM PROPS AND THEN PROPS.SUBMISSION_STATUS IS UPDATED ON "MOVE BACK TO DRAFT"
  const [submittionStatus, setSubmittionStatus] = useState(
    props.submissionStatus
  );

  // WORKFLOW CONFIRMATION MODAL
  const [isStatusModalVisible, setStatusModalVisible] =
    useState<boolean>(false);
  const [isListRefreshableChanges, setListRefreshableChanges] =
    useState<boolean>(false); // STATE VARIABLE TO TRACK IF OVERALL RECORD LIST NEEDS TO GET REFRESHED
  const [isPasswordValidated, setPasswordValidated] = useState<boolean>(false);
  const [password, setPassword] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");

  const handlePasswordValidation = async () => {
    if (!password) {
      setPasswordError("Password is required.");
      return;
    }
    try {
      // setIsLoading(true);
      const payload = {
        data: {
          password: EncryptUtils.encrypt(password),
        },
      };
      const response = await post(ValidateReEditPassword, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        setPasswordValidated(true);
        setPasswordError("");
      } else {
        setPasswordError(response.dataResponse.description);
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error validating password:", error);
      setPasswordError("An unexpected error occurred. Please try again.");
    } finally {
      // setIsLoading(false);
    }
  };

  // FUNCTION TO CONVERT A FILE TO BASE64
  const getBase64 = (file: RcFile): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  // TO INITIALIZE THE IMAGE PREVIEW DETAILS
  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj as RcFile);
    }

    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
    setPreviewTitle(
      file.name || file.url!.substring(file.url!.lastIndexOf("/") + 1)
    );
  };

  useEffect(() => {
    fetchCustomerOptions();
    fetchStatorOptions();
  }, []);

  useEffect(() => {
    fetchDefectLogBook();
  }, [customer]);

  // TO FETCH ALL THE CUSTOMER OPTIONS
  const fetchCustomerOptions = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetCustomerList, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // TRANSFORM DATA TO MATCH TABLE COLUMNS
        setCustomerOptions(Utils.mapDropdownOptions(data));
        if (!props?.id) {
          form.setFieldValue(
            "customer",
            Utils.mapDropdownOptions?.(data)?.[0]?.value
          );
        }
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      // onShowToast({
      //   type: ToastType.error,
      //   title: <CloseCircleFilled />,
      //   position: ToastOpen.leftBottom,
      //   content: "Failed to fetch customer data",
      // });
      setIsLoading(false);
    }
  };

  // TO FETCH ALL THE STATOR OPTIONS
  const fetchStatorOptions = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetStatorUsedList, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        // TRANSFORM DATA TO MATCH TABLE COLUMNS
        setStatorOptions(Utils.mapDropdownOptions(data, "id", "statorUsed"));
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      // onShowToast({
      //   type: ToastType.error,
      //   title: <CloseCircleFilled />,
      //   position: ToastOpen.leftBottom,
      //   content: "Failed to fetch customer data",
      // });
      setIsLoading(false);
    }
  };

  // TO FETCH THE PATH OF DEFECT CODE BOOK AS PER CUSTOMER
  const fetchDefectLogBook = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: 1,
          customerId: customer,
          isActive: 1,
        },
      };

      const response = await post(GetDefectLogBook, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setDefectLogBookPath(data?.[0]?.path);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
      }
    } catch (error) {
      // onShowToast({
      //   type: ToastType.error,
      //   title: <CloseCircleFilled />,
      //   position: ToastOpen.leftBottom,
      //   content: "Failed to fetch customer data",
      // });
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  // COMMON FUNCTION TO ADD NEW LOGS IN STATE VARIABLE
  const addLog = useCallback(
    (message: string, fieldName?: string, stageName?: string) => {
      setLogs((prevLogs) => {
        const timeStamp = new Date();
        const newLog = { message, timeStamp, fieldName, stageName };
        let updatedLogs;

        if (fieldName) {
          const existingLogIndex = prevLogs.findIndex(
            (log) => log.fieldName === fieldName
          );
          if (existingLogIndex > -1) {
            if (prevLogs[existingLogIndex].message === message) {
              return prevLogs; // No change
            }
            updatedLogs = [...prevLogs];
            updatedLogs[existingLogIndex] = newLog;
          } else {
            updatedLogs = [...prevLogs, newLog];
          }
        } else {
          if (prevLogs.some((log) => log.message === message)) {
            return prevLogs; // No change
          }
          updatedLogs = [...prevLogs, newLog];
        }

        // onShowToast({
        //   type: ToastType.info,
        //   title: <InfoCircleFilled />,
        //   position: ToastOpen.leftBottom,
        //   content: message,
        // });
        setOuputMessage(message);
        showOuputMsgModal(true);
        return updatedLogs;
      });
      // Trigger form submission which calls onFinish for real-time save
      // setTimeout(() => form.submit(), 100);
    },
    [context?.user?.dateFormat]
  );

  // REF FUNCTION TO TRIGGER THE ONFINISH FUNCTION
  const scheduleSubmission = useCallback(() => {
    if (submissionTimeoutRef.current) {
      clearTimeout(submissionTimeoutRef.current);
    }
    submissionTimeoutRef.current = setTimeout(() => {
      form.submit();
    }, 500); // Debounce time
  }, [form]);

  useEffect(() => {
    // TO GET THE LIST OF ALL THE FAULT CATEGORIES
    fetchFaultCategoryList();

    // WILL INITIALIZE THE ENTIRE FORM DETAILS IF EDIT CASE
    if (props.id) {
      setL1FormId(props?.id as number);
      fetchSpecificEval(Number(props?.id));
    }
  }, []);

  useEffect(() => {
    // Case: Start Eval - L1 form from overall records
    if (props.customerId && props.serialNumber && props.productId) {
      form.setFieldValue("customer", props.customerId);
      form.setFieldValue("typeOfUnit", props?.productId);
      form.setFieldValue("modelNoOfUnit", props?.productId);
      form.setFieldValue("productId", props?.productId);
      setInputSerialNo(props.serialNumber);
    }
    props?.startEvalL1 && fetchScanData(props?.serialNumber as string);
  }, [props]);

  useEffect(() => {
    // FETCH THE PRODUCT OPTION ON THE BASIS OF CUSTOMER SELECTION
    fetchProductOptions();
  }, [customer]);

  // SAVE THE USER PREFERENCE IN LOCAL STORAGE AND INITIALIZE THE SAME IF ALREADY PRESENT
  useEffect(() => {
    localStorage.setItem("showSystemLogs", JSON.stringify(showLogs));
  }, [showLogs]);

  // LOGIC TO CHECK IF MANUF DATE IS OLDER THAN ONE YEAR OR NOT
  const isOlderThanOneYear = useMemo(() => {
    if (scanResult?.manufacturingDate) {
      const mfgDate = dayjs(scanResult.manufacturingDate);
      return dayjs().diff(mfgDate, "day") > 365;
    }
    return false;
  }, [scanResult]);

  // FUNCTION TO HANDLE THE HI-POT FIELD CHANGE EVENT
  const handleHiPotStatusChange = (value: eHIPOT) => {
    form.setFieldsValue({
      rustInConnectorStatus: null,
      noOfStatorUsedId: null,
      performanceTestingStatus: null,
      testStandStatus: null,
      faultCategoryId: null,
      signOfCarbonResidue: null,
      cutInUnitStatus: null,
      rustImgDescription: "",
      visualImgDescription: "",
      carbonImgDescription: "",
    });
    setRustInConnectorImages([]);
    setCarbonResidueImages([]);
    setDefectImages([]);
    setLogs((prev) => prev.filter((l) => l.fieldName === "scanResult"));
    // ACTION LOGS -- DON'T NEED IN OUPUT MESSAGES
    if (value) {
      //   addLog(`Hi-Pot Status: ${eHIPOT[value]}`, "hiPotStatus");
      if (value === eHIPOT.FAIL) {
        addLog(
          eL1OUTPUTMSGS.HIPOTFAIL,
          "hiPotStatusOuput",
          eL1_STAGES_NAME[eL1_STAGES.HI_POT]
        );
      }
    }
    setStage(eL1_STAGES.HI_POT);
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE RUST IN CONNECTOR FIELD CHANGE EVENT
  const handleRustInConnectorStatusChange = (value: eRUSTINCONNECTOR) => {
    if (value) {
      form.setFieldsValue({
        rustImgDescription: "",
        visualImgDescription: "",
        carbonImgDescription: "",
      });
      setRustInConnectorImages([]);
      setCarbonResidueImages([]);
      setDefectImages([]);
      setLogs((prev) =>
        prev.filter(
          (l) =>
            l.fieldName === "scanResult" || l.fieldName === "hiPotStatusOuput"
        )
      );
      // ACTION LOGS -- DON'T NEED IN OUPUT MESSAGES
      // addLog(
      //   `Rust in Connector: ${eRUSTINCONNECTOR[value]}`,
      //   "rustInConnectorStatus"
      // );
      if (value === eRUSTINCONNECTOR.YES) {
        setCurrentStatus(eOVERALLISTSTATUS.TEAR_DOWN);
        addLog(
          eL1OUTPUTMSGS.RUSTINCONNECTORYES,
          "rustInConnectorStatusOutput",
          eL1_STAGES_NAME[eL1_STAGES.HI_POT]
        );
      }
      if (value === eRUSTINCONNECTOR.NO) {
        setCurrentStatus(eOVERALLISTSTATUS.EVAL_L2);
        addLog(
          eL1OUTPUTMSGS.RUSTINCONNECTORNO,
          "rustInConnectorNo",
          eL1_STAGES_NAME[eL1_STAGES.HI_POT]
        );
      }
    }
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE NO OF STATOR USED FIELD CHANGE EVENT
  const handleNoOfStatorUsedIdChange = (value: eNOOFTIMESSTATORUSED) => {
    form.setFieldsValue({
      performanceTestingStatus: null,
      testStandStatus: null,
      faultCategoryId: null,
      signOfCarbonResidue: null,
      cutInUnitStatus: null,
      visualImgDescription: "",
      rustImgDescription: "",
      carbonImgDescription: "",
    });

    setRustInConnectorImages([]);
    setCarbonResidueImages([]);
    setDefectImages([]);

    setLogs((prev) =>
      prev.filter(
        (l) => l.fieldName === "scanResult" || l.fieldName === "hiPotStatus"
      )
    );
    if (value) {
      // addLog(
      //   `No. of times Stator Used: ${eNOOFTIMESSTATORUSED[value].replace(
      //     /_/g,
      //     " "
      //   )}`,
      //   "noOfStatorUsedId"
      // );
      addLog(
        eL1OUTPUTMSGS.NOOFTIMESSTATORUSED,
        "noOfTimesStatorUsedOuput",
        eL1_STAGES_NAME[eL1_STAGES.HI_POT]
      );
      // addLog(eL1OUTPUTMSGS.PERFORMANCETESTING, "performanceTestingProcess");
    }
    setStage(eL1_STAGES.HI_POT);
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE PERFORMANCE TESTING FIELD CHANGE EVENT
  const handlePerformanceTestingStatusChange = (value: ePERFORMANCETESTING) => {
    form.setFieldsValue({
      testStandStatus: null,
      faultCategoryId: null,
      signOfCarbonResidue: null,
      cutInUnitStatus: null,
      carbonImgDescription: "",
    });
    setCarbonResidueImages([]);
    setLogs((prev) =>
      prev.filter(
        (l) =>
          l.fieldName === "scanResult" ||
          l.fieldName === "hiPotStatus" ||
          l.fieldName === "noOfStatorUsedId" ||
          l.fieldName === "noOfTimesStatorUsedOuput"
      )
    );
    if (value) {
      // addLog(
      //   `Performance Testing: ${ePERFORMANCETESTING[value].replace(/_/g, " ")}`,
      //   "performanceTestingStatus"
      // );
      if (value === ePERFORMANCETESTING.NO_FAULT_FOUND) {
        if (isOlderThanOneYear) {
          addLog(
            `Unit Manuafcturing date: ${DateUtils.format(scanResult?.manufacturingDate, context?.user?.dateFormat, false)}. ${eL1OUTPUTMSGS.OLDERTHANYEAROLD}`,
            "olderThanYearOutput",
            eL1_STAGES_NAME[eL1_STAGES.PERFORMANCE_TESTING]
          );
          setCurrentStatus(eOVERALLISTSTATUS.TEAR_DOWN);
        } else {
          addLog(
            `Unit Manuafcturing date: ${DateUtils.format(scanResult?.manufacturingDate, context?.user?.dateFormat, false)}. ${eL1OUTPUTMSGS.LESSTHANYEAROLD}`,
            "lessThanYearOutput",
            eL1_STAGES_NAME[eL1_STAGES.PERFORMANCE_TESTING]
          );
        }
      }
    }
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE FAULT CATEGORY FIELD CHANGE EVENT
  const handleFaultCategoryIdChange = (value: number) => {
    form.setFieldsValue({
      signOfCarbonResidue: null,
      cutInUnitStatus: null,
      carbonImgDescription: "",
    });
    setCarbonResidueImages([]);
    // if (value) {
    //   addLog(eL1OUTPUTMSGS.DISPOSITION_PHASE, "faultCategoryId");
    // }
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE TEST STATUS FIELD CHANGE EVENT
  const handleTestStandStatusChange = (value: eTESTSTANDRESULT) => {
    if (value) {
      // addLog(
      //   `Test Stand Result: ${eTESTSTANDRESULT[value].replace(/_/g, " ")}`,
      //   "testStandStatus"
      // );
      setCarbonResidueImages([]);
      if (value === eTESTSTANDRESULT.NO_FAULT_FOUND) {
        addLog(
          eL1OUTPUTMSGS.TESTSTANDNOFAULT,
          "restStandResultOuptut",
          eL1_STAGES_NAME[eL1_STAGES.PERFORMANCE_TESTING]
        );
        setCurrentStatus(eOVERALLISTSTATUS.NO_FAULT);
      }
      if (value === eTESTSTANDRESULT.FAULT_FOUND) {
        addLog(
          eL1OUTPUTMSGS.UNITMOVETOTEARDOWN,
          "restStandResultOuptut",
          eL1_STAGES_NAME[eL1_STAGES.PERFORMANCE_TESTING]
        );
        setCurrentStatus(eOVERALLISTSTATUS.TEAR_DOWN);
      }
    }
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE SIGN OF CARBON RESIDUE FIELD CHANGE EVENT
  const handleSignOfCarbonResidueChange = (value: eSIGNOFCARBON) => {
    form.setFieldsValue({
      cutInUnitStatus: null,
      carbonResidueVerification: null,
      carbonImgDescription: "",
    });
    setCarbonResidueImages([]);
    setLogs((prev) =>
      prev.filter(
        (l) =>
          l.fieldName === "scanResult" ||
          l.fieldName === "hiPotStatus" ||
          l.fieldName === "noOfStatorUsedId" ||
          l.fieldName === "noOfTimesStatorUsedOuput"
      )
    );
    if (value) {
      // addLog(
      //   `Sign of Carbon Residue: ${eSIGNOFCARBON[value]}`,
      //   "signOfCarbonResidue"
      // );
      if (value === eSIGNOFCARBON.YES) {
        addLog(
          eL1OUTPUTMSGS.CARBONRESIDUEYES,
          "carbonResidueOuput",
          eL1_STAGES_NAME[eL1_STAGES.DISPOSITION]
        );
      }
    }
    scheduleSubmission();
  };

  // FUNCTION TO HANDLE THE CUT IN UNIT FIELD CHANGE EVENT
  const handleCutInUnitStatusChange = (value: eVERIFYCUTINUNIT) => {
    if (value) {
      // addLog(`Cut In Unit: ${eVERIFYCUTINUNIT[value]}`, "cutInUnitStatus");
      setCarbonResidueImages([]);
      setLogs((prev) =>
        prev.filter(
          (l) =>
            l.fieldName === "scanResult" ||
            l.fieldName === "hiPotStatus" ||
            l.fieldName === "noOfStatorUsedId" ||
            l.fieldName === "noOfTimesStatorUsedOuput" ||
            l.fieldName === "carbonResidueOuput"
        )
      );
      if (value === eVERIFYCUTINUNIT.YES) {
        addLog(
          eL1OUTPUTMSGS.AUTOPSYEVALUTION,
          "cutInUnitOutput",
          eL1_STAGES_NAME[eL1_STAGES.DISPOSITION]
        );
        setCurrentStatus(eOVERALLISTSTATUS.EVAL_L2);
      }
      if (value === eVERIFYCUTINUNIT.NO) {
        if (carbonResidueVerification === eCARBONRESIDUEVERIFICATION.YES) {
          addLog(
            eL1OUTPUTMSGS.NOCUTINUNIT,
            "cutInUnitOuput",
            eL1_STAGES_NAME[eL1_STAGES.DISPOSITION]
          );
        } else {
          addLog(
            eL1OUTPUTMSGS.READYTOTEARDOWN,
            "cutInUnitOuput",
            eL1_STAGES_NAME[eL1_STAGES.DISPOSITION]
          );
        }
        setCurrentStatus(eOVERALLISTSTATUS.TEAR_DOWN);
      }
    }
    scheduleSubmission();
  };
  // FUNCTION TO HANDLE THE CUT IN UNIT FIELD CHANGE EVENT
  const handleVerifyCarbonResidueChange = (
    value: eCARBONRESIDUEVERIFICATION
  ) => {
    if (value) {
      form.setFieldsValue({
        cutInUnitStatus: null,
      });
      setLogs((prev) =>
        prev.filter(
          (l) =>
            l.fieldName === "scanResult" ||
            l.fieldName === "hiPotStatus" ||
            l.fieldName === "noOfStatorUsedId" ||
            l.fieldName === "noOfTimesStatorUsedOuput" ||
            l.fieldName === "carbonResidueOuput"
        )
      );
    }
    scheduleSubmission();
  };

  // TO FETCH THE UNIT AND MODEL FIELD OPTIONS
  const fetchProductOptions = async () => {
    if (customer) {
      try {
        setIsLoading(true);
        const requestPayload = {
          data: {
            currentPage: 1,
            pageSize: -1,
            customerId: customer,
          },
        };

        const response = await post(GetProductList, requestPayload);
        const { dataResponse, data } = response;

        if (dataResponse.returnCode === eResultCode.SUCCESS) {
          const formattedUnitOptions = data?.map((product: TProduct) => ({
            label: `${product.typeOfUnit}`,
            value: product.id,
          }));
          const formattedmodelOptions = data?.map((product: TProduct) => ({
            label: `${product.bePartNo}`,
            value: product.id,
          }));
          const productOptions = data?.map((product: TProduct) => ({
            label: `${product.airtechModel}`,
            value: product.id,
          }));
          setProductOptions(productOptions);
          setUnitOptions(formattedUnitOptions);
          setModelOptions(formattedmodelOptions);
          setIsLoading(false);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseCircleFilled />,
            position: ToastOpen.leftBottom,
            content: dataResponse.description,
          });
          setIsLoading(false);
        }
      } catch (error) {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Failed to fetch product data",
        });
        setIsLoading(false);
      }
    }
  };

  // TO FETCH THE SPECIFIC EVALUATION DETAILS AND INITIALIZE THE ENTIRE FORM VALUES.
  const fetchSpecificEval = async (id: number) => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          id: id,
        },
      };

      const response = await post(GetSpecificLeve1EvalDetails, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setSubmittionStatus(data.submissionStatus);
        // FILTER FOR VALUE IS 0
        const replaceZeroWithNull = Object.fromEntries(
          Object.entries(data || {}).map(([key, value]) => [
            key,
            value === 0 ? null : value,
          ])
        );

        // INITIALIZE THE FORM FIELDS VALUES
        form.setFieldsValue({
          ...replaceZeroWithNull,
          customer: replaceZeroWithNull.customerId,
          typeOfUnit: replaceZeroWithNull.productId,
          modelNoOfUnit: replaceZeroWithNull.productId,
          productId: replaceZeroWithNull.productId,
        });
        setInputSerialNo(data?.unitSerialNumber);
        setEvalDate(data?.evaluationDate);
        setCurrentStatus(data?.draftCurrentStageId);
        // TO FETCH SCAN DETAILS OF SCANNED UNIT SERIAL NUMBER
        const scanRes = await fetchScanData(data?.unitSerialNumber, true, true);

        // ADD THE SCANRESULT OBJECT INTO LOGS AS 1ST LOG WITH FIELDNAME "scanResult" IS VERY DETAILED -- DIFFERENT THAN OTHER LOGS
        const responseLogs = data?.logs?.map((log: TLogEntry) => {
          return {
            ...log,
            scanResult: log.fieldName === "scanResult" ? scanRes : null,
          };
        });
        setLogs(responseLogs);

        // SHOW ALL THE POSSIBLE STAGES(ACCORDION) AND SET ALL THE STAGE AS ACTIVE -- AS UNABLE TO IDENTIFY THE CURRENT ACTIVE STAGE
        setStage(eL1_STAGES.DISPOSITION);
        setActiveKey([
          eL1_STAGES.EVAL_L1_SCAN,
          eL1_STAGES.HI_POT,
          eL1_STAGES.VISUAL_INSPECTION,
          eL1_STAGES.PERFORMANCE_TESTING,
          eL1_STAGES.DISPOSITION,
        ]);

        // TO FILTER ALL THE UPLOADED IMAGES DOCUMENTS IN DESIRED STATE VARIABLE
        if (data.documents && data.documents.length > 0) {
          const rustImages: UploadFile[] = [];
          const defectImages: UploadFile[] = [];
          const carbonImages: UploadFile[] = [];

          data.documents.forEach((doc: any) => {
            const file: any = {
              uid: doc.id.toString(),
              name: doc.name,
              status: "done",
              url: doc.path,
              isUploaded: true,
            };

            switch (doc.docType) {
              case eEVAL_L1_IMAGES.RUST_IN_CONNECTOR:
                rustImages.push(file);
                break;
              case eEVAL_L1_IMAGES.VISUAL_INSPECTION:
                defectImages.push(file);
                break;
              case eEVAL_L1_IMAGES.CARBON_RESIDUE:
                carbonImages.push(file);
                break;
              default:
                break;
            }
          });

          setRustInConnectorImages(rustImages);
          setDefectImages(defectImages);
          setCarbonResidueImages(carbonImages);
        }

        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch product data",
      });
      setIsLoading(false);
    }
  };

  // TO FETCH ALL THE FAULT CATEGORIES OPTIONS
  const fetchFaultCategoryList = async () => {
    try {
      setIsLoading(true);
      const requestPayload = {
        data: {
          currentPage: 1,
          pageSize: -1,
        },
      };

      const response = await post(GetFaultCategoryList, requestPayload);
      const { dataResponse, data } = response;

      if (dataResponse.returnCode === eResultCode.SUCCESS) {
        setFaultCategoryOptions(
          Utils.mapDropdownOptions(data, "id", "problemName")
        );
        setIsLoading(false);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: dataResponse.description,
        });
        setIsLoading(false);
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseCircleFilled />,
        position: ToastOpen.leftBottom,
        content: "Failed to fetch product data",
      });
      setIsLoading(false);
    }
  };

  // FUNCTIONALITY TO HANDLE THE INPUT SERIAL NO ONCHANGE EVENT
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputSerialNo(e.target.value.trim().toUpperCase());
  };

  // FUNCTION TO HANDLE THE SEARCH EVENT ON INPUT SERIAL NO. FIELD
  const handleSearch = (value: string) => {
    setStage(eL1_STAGES.EVAL_L1_SCAN);
    handleApiCall(value);
    form.setFieldsValue({
      hiPotStatus: null,
      rustInConnectorStatus: null,
      noOfStatorUsedId: null,
      performanceTestingStatus: null,
      testStandStatus: null,
      faultCategoryId: null,
      signOfCarbonResidue: null,
      cutInUnitStatus: null,
    });

    setLogs((prev) => prev.filter((l) => l.fieldName === "scanResult"));
  };

  // FUNCTION TO HANDLE THE UNIT CHANGES
  const handleUnitChange = (
    value: number,
    field: "typeOfUnit" | "modelNoOfUnit" | "productId"
  ) => {
    const otherField = field === "typeOfUnit" ? "modelNoOfUnit" : "typeOfUnit";
    form.setFieldsValue({ [otherField]: value });
    setStage(eL1_STAGES.EVAL_L1_SCAN);
  };

  // API METHOD TO GET THE DETAILS OF UNIT SERAIL NO. SCANNED RESULT
  const fetchScanData = useCallback(
    async (
      serial: string,
      ret: boolean = false,
      isLevel1GetSpecificRequest: boolean = false
    ) => {
      try {
        const requestPayload = {
          data: {
            serialNumber: serial,
            productId: form.getFieldValue("typeOfUnit"),
            isLevel1Scan: true,
            customerId: customer,
            isProductRetryRequest: false, // SET FALSE FOR THE FIRST REQUEST -- TRUE :: WILL BYPASS THE VALIDATION
            isOldSerialNoRetryRequest: false, // SET FALSE FOR THE FIRST REQUEST -- TRUE :: WILL BYPASS THE VALIDATION
            isLevel1GetSpecificRequest:
              props?.id || isLevel1GetSpecificRequest ? true : false, // WILL BYPASS ALL THE VALIDATION CHECK IN BACKEND
          },
        };
        const response: TResponseModel = await post(
          GetScannedProductDetails,
          requestPayload
        );
        let { dataResponse, data } = response;
        if (
          dataResponse.returnCode === eResultCode.SUCCESS ||
          dataResponse.returnCode === eResultCode.R_DUPLICATE
        ) {
          const scanRes = {
            ...data,
            responseCode: dataResponse?.returnCode,
            responseMsg: dataResponse.description,
            loggedOn: new Date(),
          };
          setScanResult(scanRes);
          if (data) {
            const timeStamp = new Date();
            const message =
              data.warrantyStatus === 1
                ? eL1OUTPUTMSGS.CLAIMEDANDINWARRANTY
                : eL1OUTPUTMSGS.NOTWARRANTYNOTCLAIMED;

            // const stageName =
            //   data.warrantyStatus === eL1_STAGES.EVAL_L1_SCAN
            //     ? eL1_STAGES_NAME.[eL1_STAGES.EVAL_L1_SCAN]
            //     : eL1OUTPUTMSGS.NOTWARRANTYNOTCLAIMED;

            const newLog: TLogEntry = {
              message,
              timeStamp,
              fieldName: "scanResult",
              scanResult: data,
              stageName: eL1_STAGES_NAME[eL1_STAGES.EVAL_L1_SCAN],
            };

            setLogs([newLog]);
            // if (!isLevel1GetSpecificRequest) {
            //   scheduleSubmission();
            // }
          } else {
            setLogs([]);
          }
          if (ret) {
            return scanRes;
          }
        } else if (
          dataResponse.returnCode === eResultCode.R_SHOWPRODUCTRETRYALERT
        ) {
          // SHOW CONFIRMATION MODAL LOGIC HERE
          setShowErrorModal(true);
          setErrorMessage(dataResponse?.description);
          setLogs([]);
        } else if (
          dataResponse.returnCode === eResultCode.R_SHOWOLDSERIALRNORETRYALERT
        ) {
          // show confirmation modal logic here
          setShowOldSerialModal(true);
          setErrorMessage(dataResponse?.description);
          setLogs([]);
        } else if (dataResponse.returnCode === eResultCode.R_SHOWL1EVALDATA) {
          // SHOW ALREADY PROCESSED MODAL CONFIRMATION MODAL LOGIC HERE
          showAlreadyProecessedModal(true);
          setAlreadyProcessedMsg(dataResponse?.description);
          setL1FormId(data?.level1EvalId);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: dataResponse.description,
          });
        }
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false);
      }
    },
    []
  );

  // FUNCTION TO SCAN THE UNIT SERIAL NO. BUT WITH FORCED ACTION :: TO BYPASS ALL THE VALIDATIONS
  const fetchForcedScanData = useCallback(
    async (
      pId: number,
      sNo: string,
      cId: number,
      isOldSerialForced: boolean = false
    ) => {
      try {
        const requestPayload = {
          data: {
            serialNumber: sNo,
            productId: pId,
            isLevel1Scan: true,
            customerId: cId,
            isProductRetryRequest: !isOldSerialForced ? true : false,
            isOldSerialNoRetryRequest: isOldSerialForced ? true : false,
          },
        };
        const response: TResponseModel = await post(
          GetScannedProductDetails,
          requestPayload
        );
        let { dataResponse, data } = response;
        if (
          dataResponse.returnCode === eResultCode.SUCCESS ||
          dataResponse.returnCode === eResultCode.R_DUPLICATE
        ) {
          const scanRes = {
            ...data,
            responseCode: dataResponse?.returnCode,
            responseMsg: dataResponse.description,
            loggedOn: new Date(),
          };
          setScanResult(scanRes);
          if (data) {
            const timeStamp = new Date();
            const message =
              data.warrantyStatus === 1
                ? eL1OUTPUTMSGS.CLAIMEDANDINWARRANTY
                : eL1OUTPUTMSGS.NOTWARRANTYNOTCLAIMED;

            const newLog: TLogEntry = {
              message,
              timeStamp,
              fieldName: "scanResult",
              scanResult: data,
            };

            setLogs([newLog]);
            // scheduleSubmission();
          } else {
            setLogs([]);
          }
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: dataResponse.description,
          });
        }
      } catch (error) {
        console.error("Error:", error);
        setIsLoading(false);
      } finally {
        handleDeleteCancel();
      }
    },
    []
  );

  // FUNCTION TO CALL ON CLICK OF CANCEL ON ERROR MODAL ::
  const handleDeleteCancel = () => {
    setShowErrorModal(false);
    setShowOldSerialModal(false);
    setErrorMessage("");
    // setInputSerialNo("");
  };

  // CALLBACK FUNCTION TO PREVENT THE RE-RENDERING OF FETCHSCANDATA FUNCTION
  const handleApiCall = useCallback(
    async (serial: string) => {
      if (!serial) return;
      setIsLoading(true);
      setError(null);
      setScanResult(null);
      try {
        await fetchScanData(serial);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchScanData]
  );

  const outputStyle = useMemo((): React.CSSProperties => {
    if (!scanResult) return {};
    return {
      backgroundColor:
        scanResult.outputStatus === eOUTPUT_STATUS.GREEN
          ? "var(--bs-success)"
          : "var(--bs-danger)",
      color: "white",
      textAlign: "center",
      padding: "0.5rem",
      borderRadius: "8px",
      transition: "background-color 0.3s ease",
      marginBottom: "1rem",
    };
  }, [scanResult]);

  // HANDLE ON SUBMIT EVENT
  const onFinish = async (
    values: TL1S1,
    status: eEVAL_L1_WORKFLOW = eEVAL_L1_WORKFLOW.DRAFT
  ) => {
    if (!scanResult) return;
    try {
      const { id, ...restScans } = scanResult as TScanResult;
      const payload = {
        data: {
          ...values,
          ...restScans,
          draftCurrentStageId: currentStatus,
          currentStageId:
            status === eEVAL_L1_WORKFLOW.DRAFT ? 0 : currentStatus, // PASS 0 BECAUSE WITHOUT SUBMIT BUTTON STAGE IS UPDATED
          id: l1FormId ?? 0,
          unitSerialNumber: scanResult?.serialNumber,
          productId: values.modelNoOfUnit,
          scannedProductInfoId: id,
          evaluationDate: new Date().toDateString(),
          logs: logs ?? [],
          submissionStatus: status,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditL1Evaluation, payload);
      if (
        response.dataResponse.returnCode === eResultCode.SUCCESS &&
        response.data
      ) {
        status === eEVAL_L1_WORKFLOW.SUBMITTED &&
          setListRefreshableChanges(true); // this state variable will refresh the overall list -- if submit has detected means current record status has been changed need to force refresh the list

        setL1FormId(response?.data?.id);
        const responseLogs = response?.data?.logs?.map((log: TLogEntry) => {
          return {
            ...log,
            scanResult: log.fieldName === "scanResult" ? scanResult : null,
          };
        });
        setLogs(responseLogs);
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // HANDLE ON SUBMIT EVENT
  const moveBackAsDraft = async () => {
    try {
      const payload = {
        data: {
          id: l1FormId || props?.id,
          submissionStatus: eEVAL_L1_WORKFLOW.DRAFT,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditL1Evaluation, payload);
      if (
        response.dataResponse.returnCode === eResultCode.SUCCESS &&
        response.data
      ) {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
        if (props.id) {
          await fetchSpecificEval(Number(props.id));
        }
        setStatusModalVisible(false);
        setListRefreshableChanges(true);
        // props?.onCloseDrawer?.();
        // props?.onRefreshList?.();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseCircleFilled />,
          position: ToastOpen.leftBottom,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      onFinish(values, eEVAL_L1_WORKFLOW.SUBMITTED).then(() => {
        onShowToast({
          type: ToastType.success,
          title: <CheckCircleFilled />,
          position: ToastOpen.leftBottom,
          content: "Evaluation submitted successfully.",
        });
        form.resetFields();
        form.setFieldValue("customer", customerOptions[0].value);
        setScanResult(null);
        setLogs([]);
        setStage(eL1_STAGES.EVAL_L1_SCAN);
        setActiveKey([eL1_STAGES.EVAL_L1_SCAN]);
        setInputSerialNo("");
        setDefectImages([]);
        setRustInConnectorImages([]);
        setCarbonResidueImages([]);
        props?.onCloseDrawer?.();
        props?.onRefreshList?.();
      });
    });
  };

  const handleUploadImages = async (type: eEVAL_L1_IMAGES) => {
    if (
      type === eEVAL_L1_IMAGES.CARBON_RESIDUE &&
      carbonResidueImages.length === 0
    ) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Please select images of carbon residue ",
      });
      return;
    }
    if (
      type === eEVAL_L1_IMAGES.RUST_IN_CONNECTOR &&
      rustInConnectorImages.length === 0
    ) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Please select images of rust",
      });
      return;
    }
    if (
      type === eEVAL_L1_IMAGES.VISUAL_INSPECTION &&
      defectImages.length === 0
    ) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Please select images of found defect",
      });
      return;
    }

    // Implement your file upload logic here
    const formData = new FormData();
    // --- Determine the correct array of images based on 'type' ---
    let imageFiles;

    if (type === eEVAL_L1_IMAGES.RUST_IN_CONNECTOR) {
      imageFiles = rustInConnectorImages;
    } else if (type === eEVAL_L1_IMAGES.VISUAL_INSPECTION) {
      imageFiles = defectImages;
    } else {
      imageFiles = carbonResidueImages;
    }

    // --- Fix: Loop through the array and append each file with structured metadata ---
    if (Array.isArray(imageFiles)) {
      imageFiles.forEach((file: UploadFile) => {
        if (!(file as any).isUploaded && file.originFileObj) {
          formData.append("Data.Images", file.originFileObj);
        }
      });
    }
    formData.append("Data.DocType", type?.toString());
    formData.append("Data.EvaluationId", l1FormId?.toString() || "");
    formData.append("Data.EvaluationType", "1");

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${UploadDocument}`,
        {
          method: "POST",
          body: formData,

          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );
      if (response.status === eHTTPStatusCode.OK) {
        const responseData = (await response.json()) as TResponseModel;
        if (responseData.dataResponse.returnCode === eResultCode.SUCCESS) {
          onShowToast({
            type: ToastType.success,
            title: <CheckOutlined />,
            position: ToastOpen.leftBottom,
            content: responseData.dataResponse.description,
          });
          if (type === eEVAL_L1_IMAGES.CARBON_RESIDUE) {
            setCarbonResidueImages((prev) =>
              prev.map((file) => ({ ...file, isUploaded: true }) as UploadFile)
            );
          } else if (type === eEVAL_L1_IMAGES.RUST_IN_CONNECTOR) {
            setRustInConnectorImages((prev) =>
              prev.map((file) => ({ ...file, isUploaded: true }) as UploadFile)
            );
          } else if (type === eEVAL_L1_IMAGES.VISUAL_INSPECTION) {
            setDefectImages((prev) =>
              prev.map((file) => ({ ...file, isUploaded: true }) as UploadFile)
            );
          }
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.leftBottom,
            content: responseData.dataResponse.description,
          });
        }
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.leftBottom,
          content: UNAUTHORIZEDREQUEST,
        });
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.leftBottom,
        content: "Error uploading file",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const areRustImagesUploaded =
    rustInConnectorImages.length > 0 &&
    rustInConnectorImages.every((f) => (f as any).isUploaded);
  // const areDefectImagesUploaded =
  //   defectImages.length > 0 && defectImages.every((f) => (f as any).isUploaded);
  const areCarbonResidueImagesUploaded =
    carbonResidueImages.length > 0 &&
    carbonResidueImages.every((f) => (f as any).isUploaded);

  return (
    <Row className={styles.parentContainer}>
      <Modal
        open={previewOpen}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewOpen(false)}
        width={800}
      >
        <img alt="example" style={{ width: "100%" }} src={previewImage} />
      </Modal>
      <CustomModal
        title="Confirm"
        isOpen={showErrorModal}
        onOk={async () =>
          await fetchForcedScanData(
            form.getFieldValue("typeOfUnit"),
            inputSerialNo,
            customer
          )
        }
        onCancel={() => handleDeleteCancel()}
        okText="Proceed"
        cancelText="Close"
        cusHeader={true}
      >
        <p>{errorMessage}</p>
      </CustomModal>
      <CustomModal
        title="Confirm"
        isOpen={showOldSerialModal}
        onOk={async () =>
          await fetchForcedScanData(
            form.getFieldValue("typeOfUnit"),
            inputSerialNo,
            customer,
            true
          )
        }
        onCancel={() => handleDeleteCancel()}
        okText="Proceed"
        cancelText="Close"
        cusHeader={true}
      >
        <p>{errorMessage}</p>
      </CustomModal>
      <CustomModal
        title="Output Message"
        isOpen={outputMsgModal}
        onOk={() => {}}
        onCancel={() => showOuputMsgModal(false)}
        okText=""
        hideOkButton={true}
        cancelText="Ok"
        cusHeader={true}
      >
        <p>{outputMessage}</p>
      </CustomModal>

      {/* ALREADY PROCESSED SERIAL NUMBER */}
      <CustomModal
        title="Serial Number Already Processed"
        isOpen={alreadyProcessedModal}
        onOk={async () => {
          await fetchSpecificEval(l1FormId as number);
          showAlreadyProecessedModal(false);
        }}
        onCancel={() => showAlreadyProecessedModal(false)}
        okText="Proceed"
        hideOkButton={false}
        cancelText="Cancel"
        cusHeader={false}
      >
        <p>{alreadyProcessMsg}</p>
      </CustomModal>

      <CustomModal
        title="Confirm Action"
        isOpen={isStatusModalVisible}
        onOk={async () => await moveBackAsDraft()}
        onCancel={() => {
          setStatusModalVisible(false);
          setPassword("");
          setPasswordError("");
          setPasswordValidated(false);
        }}
        hideOkButton={!isPasswordValidated}
        okText="Proceed"
        cancelText="Cancel"
        cusHeader={true}
      >
        {!isPasswordValidated ? (
          <>
            <p>
              You are about to move the record associate with{" "}
              <b>{scanResult?.serialNumber} </b>back to draft?
            </p>
            <Form.Item
              label="Please enter the password to proceed"
              validateStatus={passwordError ? "error" : ""}
              help={passwordError}
            >
              <Input.Password
                placeholder="Enter password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError("");
                }}
                onPressEnter={handlePasswordValidation}
              />
            </Form.Item>
            <Button
              type="primary"
              onClick={handlePasswordValidation}
              loading={isLoading}
            >
              Next
            </Button>
          </>
        ) : (
          <p>
            Password validated. Click Proceed to move the record back to draft.
          </p>
        )}
      </CustomModal>
      <Col
        xs={24}
        sm={24}
        md={showLogs ? 18 : 24}
        lg={showLogs ? 18 : 24}
        xl={showLogs ? 18 : 24}
        className={styles.leftContainer}
        style={{ borderRight: showLogs ? "1px solid #f0f0f0" : "none" }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 1rem",
            flexWrap: "wrap",
            rowGap: "8px",
          }}
        >
          <h3
            style={{
              margin: 0,
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.85)",
            }}
          >
            Eval - L1 Process
          </h3>
          <div>
            {/* show legend here */}
            {submittionStatus === eEVAL_L1_WORKFLOW.SUBMITTED && l1FormId ? (
              <Tag color="green">Saved as Submitted</Tag>
            ) : l1FormId ? (
              <Tag color="yellow">Saved as Draft</Tag>
            ) : (
              ""
            )}

            <Button
              type="link"
              onClick={() => setShowLogs(!showLogs)}
              style={{ marginRight: "1em" }}
            >
              {showLogs ? "Hide History" : "Show History"}
            </Button>

            {props?.id || props?.serialNumber ? (
              <>
                <Button
                  type="default"
                  disabled={isLoading}
                  onClick={() => {
                    if (isListRefreshableChanges) {
                      props?.onRefreshList?.();
                    }
                    props?.onCloseDrawer?.();
                  }}
                  style={{ marginRight: "1em", marginBottom: "0.5rem" }}
                >
                  Close
                </Button>
              </>
            ) : null}
            {submittionStatus === eEVAL_L1_WORKFLOW.SUBMITTED ? (
              <Button
                type="primary"
                loading={isLoading}
                disabled={isLoading}
                onClick={() => {
                  setStatusModalVisible(true);
                  setPasswordValidated(false);
                  setPassword("");
                  setPasswordError("");
                }}
              >
                Move Back to Draft
              </Button>
            ) : null}
          </div>
        </div>
        <FormLayout>
          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            layout="vertical"
            autoComplete="off"
            size="small"
            style={{ width: "100%" }}
            disabled={
              isLoading || submittionStatus === eEVAL_L1_WORKFLOW.SUBMITTED
            }
          >
            <Collapse
              activeKey={activeKey}
              onChange={(item) => {
                setActiveKey(item.map((item: string): number => Number(item)));
              }}
              style={{ marginTop: "1rem" }}
            >
              <Collapse.Panel
                header={
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      flexWrap: "wrap",
                      rowGap: "8px",
                    }}
                  >
                    <span>{eL1_STAGES_NAME[eL1_STAGES.EVAL_L1_SCAN]}</span>
                    {scanResult ? (
                      <Tag color="blue">#{scanResult.serialNumber}</Tag>
                    ) : null}
                  </div>
                }
                key={eL1_STAGES.EVAL_L1_SCAN}
              >
                <Row gutter={16}>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Descriptions
                      labelStyle={{
                        fontFamily: "Montserrat-SemiBold !important",
                      }}
                      size="small"
                      layout="vertical"
                    >
                      <Descriptions.Item label="Date of Evaluation">
                        {evalDate
                          ? DateUtils.format(
                              evalDate,
                              context?.user?.dateFormat
                            )
                          : DateUtils.format(
                              new Date(),
                              context?.user?.dateFormat
                            )}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="customer" label="Customer">
                      <Select
                        size="middle"
                        disabled={
                          isLoading ||
                          props?.startEvalL1 ||
                          submittionStatus === eEVAL_L1_WORKFLOW.SUBMITTED ||
                          customerOptions.length === 1
                        }
                        showSearch
                        placeholder="Select a customer"
                        options={customerOptions}
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                        // onChange={(value, option) => {
                        //   const selectedOption = option as {
                        //     label: string;
                        //     value: string;
                        //   };
                        //   setSelectedCustomer(selectedOption);
                        // }}
                      ></Select>
                    </Form.Item>
                  </Col>
                  {customer ? (
                    <>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item name="productId" label="Product">
                          <Select
                            disabled={
                              props?.startEvalL1 ||
                              submittionStatus ===
                                eEVAL_L1_WORKFLOW.SUBMITTED ||
                              submittionStatus === eEVAL_L1_WORKFLOW.DRAFT
                                ? true
                                : false
                            }
                            size="middle"
                            placeholder="Select product"
                            options={productOptions}
                            onChange={(value) => {
                              handleUnitChange(value, "modelNoOfUnit");
                              handleUnitChange(value, "typeOfUnit");
                            }}
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                          name="typeOfUnit"
                          label="Type of Unit"
                          dependencies={["modelNoOfUnit"]}
                        >
                          <Select
                            disabled={true}
                            size="middle"
                            placeholder="Select type of unit"
                            options={unitOptions}
                            onChange={(value) =>
                              handleUnitChange(value, "typeOfUnit")
                            }
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>

                      <Col xs={24} sm={12} md={8} lg={6}>
                        <Form.Item
                          name="modelNoOfUnit"
                          label="Model number of Unit"
                          dependencies={["typeOfUnit"]}
                        >
                          <Select
                            disabled={true}
                            size="large"
                            placeholder="Select model no of unit"
                            options={modelOptions}
                            onChange={(value) =>
                              handleUnitChange(value, "modelNoOfUnit")
                            }
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                              (option?.label ?? "")
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                          />
                        </Form.Item>
                      </Col>
                    </>
                  ) : null}

                  {typeOfUnit && modelNoOfUnit ? (
                    <Col xs={24} sm={24} md={12} lg={12}>
                      <Form.Item
                        // name="unitSerialNumber"
                        label={"Enter or Scan Serial Number"}
                      >
                        <Input
                          style={{ height: 41 }}
                          placeholder={"Scan or type serial number..."}
                          disabled={
                            props?.id || props?.startEvalL1 ? true : false
                          }
                          value={inputSerialNo}
                          onChange={handleInputChange}
                          onPressEnter={() => handleSearch(inputSerialNo)}
                          autoFocus
                          size="small"
                          suffix={
                            <Button
                              type="primary"
                              size="small"
                              onClick={() => handleSearch(inputSerialNo)}
                              disabled={
                                isLoading ||
                                props?.startEvalL1 ||
                                submittionStatus ===
                                  eEVAL_L1_WORKFLOW.SUBMITTED ||
                                submittionStatus === eEVAL_L1_WORKFLOW.DRAFT
                                  ? true
                                  : false
                              }
                              loading={isLoading}
                            >
                              Search
                            </Button>
                          }
                        />

                        {/* {isLoading && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginTop: "8px",
                            }}
                          >
                            <Spin size="small" />
                            <span style={{ marginLeft: "8px" }}>
                              Claim and warranty status verification...
                            </span>
                          </div>
                        )} */}
                        {!isLoading && scanResult && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginTop: "8px",
                              color: "var(--bs-success)",
                            }}
                          >
                            <CheckCircleFilled />
                            <span style={{ marginLeft: "8px" }}>
                              Claim verification and warranty status processing
                              completed
                            </span>
                          </div>
                        )}
                      </Form.Item>
                    </Col>
                  ) : null}
                </Row>
              </Collapse.Panel>

              {stage === eL1_STAGES.HI_POT ||
              stage === eL1_STAGES.VISUAL_INSPECTION ||
              stage === eL1_STAGES.PERFORMANCE_TESTING ||
              stage === eL1_STAGES.DISPOSITION ? (
                <Collapse.Panel
                  header={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>{eL1_STAGES_NAME[eL1_STAGES.HI_POT]}</span>
                      {hiPotStatus && (
                        <Tag color={hiPotStatus === 1 ? "success" : "error"}>
                          {eHIPOT[hiPotStatus]}
                        </Tag>
                      )}
                    </div>
                  }
                  key={eL1_STAGES.HI_POT}
                >
                  <Row gutter={16}>
                    <Col xs={24} sm={12} md={12} lg={12}>
                      <Form.Item
                        name="hiPotStatus"
                        label="Hi - Pot"
                        // rules={[
                        //   {
                        //     required: true,
                        //     message: "Hi-Pot status is required",
                        //   },
                        // ]}
                      >
                        <Select
                          size="middle"
                          placeholder="Select Hi-Pot Status"
                          onChange={handleHiPotStatusChange}
                          options={Object.entries(eHIPOT)
                            .filter(([key]) => !isNaN(Number(key)))
                            .map(([key, value]) => ({
                              label: (value as string)
                                .toLowerCase()
                                .replace(/^\w/, (c) => c.toUpperCase()),
                              value: Number(key),
                            }))}
                        />
                      </Form.Item>
                    </Col>

                    {/* Case 1: Hi-Pot status is FAIL */}
                    {hiPotStatus === eHIPOT.FAIL && (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="rustInConnectorStatus"
                          label="Rust in Connector"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Rust in Connector is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Select Rust Status"
                            onChange={handleRustInConnectorStatusChange}
                            options={Object.entries(eRUSTINCONNECTOR)
                              .filter(([key]) => !isNaN(Number(key)))
                              .map(([key, value]) => ({
                                label: (value as string)
                                  .toLowerCase()
                                  .replace(/^\w/, (c) => c.toUpperCase()),
                                value: Number(key),
                              }))}
                          />
                        </Form.Item>
                      </Col>
                    )}

                    {/* Case 2: Hi-Pot status is PASS */}
                    {hiPotStatus === eHIPOT.PASS && (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="noOfStatorUsedId"
                          label="No. of times Stator Used"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "No. of times Stator Used is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Select Stator Usage"
                            onChange={handleNoOfStatorUsedIdChange}
                            options={statorUsedOption}
                          />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>

                  {/* Case 3: Rust in Connector is YES, show image uploader */}
                  {rustInConnectorStatus === eRUSTINCONNECTOR.YES && (
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Select images of rust"
                          // rules={[{ required: true }]}
                        >
                          <Upload
                            listType="picture-card"
                            multiple
                            beforeUpload={(file) => {
                              const isImage = file.type.startsWith("image/");
                              if (!isImage) {
                                message.error(
                                  "You can only upload image files!"
                                );
                              }
                              return isImage || Upload.LIST_IGNORE;
                            }}
                            fileList={rustInConnectorImages}
                            onPreview={handlePreview}
                            onChange={({ fileList: newFileList }) => {
                              setRustInConnectorImages(
                                newFileList.map(
                                  (file) =>
                                    ({
                                      ...file,
                                      isUploaded:
                                        (file as any).isUploaded || false,
                                    }) as UploadFile
                                )
                              );
                            }}
                            showUploadList={{
                              showRemoveIcon: false,
                              showPreviewIcon: true,
                            }}
                            itemRender={(originNode, file) => {
                              const node = React.cloneElement(
                                originNode as React.ReactElement,
                                {
                                  title: "", // removes the tooltip
                                }
                              );
                              return node;
                            }}
                          >
                            <Button icon={<FileAddOutlined />}>
                              {/* Select Images */}
                            </Button>
                          </Upload>
                        </Form.Item>
                        {rustInConnectorImages.some(
                          (file) => !(file as any).isUploaded
                        ) && (
                          <Button
                            type="primary"
                            onClick={async () =>
                              await handleUploadImages(
                                eEVAL_L1_IMAGES.RUST_IN_CONNECTOR
                              )
                            }
                            style={{ marginTop: "1rem" }}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Upload Images
                          </Button>
                        )}
                      </Col>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Rust Image Description"
                          name="rustImgDescription"
                          required
                        >
                          <Input.TextArea
                            rows={4}
                            placeholder="Rust Image Description "
                            allowClear
                          />
                        </Form.Item>
                        {/* {defectLogBookPath ? (
                          <Button
                            disabled={isLoading}
                            type="default"
                            icon={<BookOutlined />}
                            onClick={() =>
                              window.open(defectLogBookPath, "_blank")
                            }
                          >
                            View Defect Code Book
                          </Button>
                        ) : null} */}
                      </Col>
                    </Row>
                  )}
                </Collapse.Panel>
              ) : null}

              {(stage === eL1_STAGES.VISUAL_INSPECTION ||
                stage === eL1_STAGES.PERFORMANCE_TESTING ||
                stage === eL1_STAGES.DISPOSITION) &&
              noOfStatorUsedId ? (
                <Collapse.Panel
                  header={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {eL1_STAGES_NAME[eL1_STAGES.VISUAL_INSPECTION]}
                      </span>
                      {/* {hiPotStatus && (
                        <Tag color={hiPotStatus === 1 ? "success" : "error"}>
                          {eHIPOT[hiPotStatus]}
                        </Tag>
                      )} */}
                    </div>
                  }
                  key={eL1_STAGES.VISUAL_INSPECTION}
                >
                  {noOfStatorUsedId ? (
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Select images of found defect"
                          // rules={[{ required: true }]}
                        >
                          <Upload
                            listType="picture-card"
                            multiple
                            showUploadList={{
                              showRemoveIcon: false,
                              showPreviewIcon: true,
                            }}
                            itemRender={(originNode, file) => {
                              const node = React.cloneElement(
                                originNode as React.ReactElement,
                                {
                                  title: "", // removes the tooltip
                                }
                              );
                              return node;
                            }}
                            beforeUpload={(file) => {
                              const isImage = file.type.startsWith("image/");
                              if (!isImage) {
                                message.error(
                                  "You can only upload image files!"
                                );
                              }
                              return isImage || Upload.LIST_IGNORE;
                            }}
                            fileList={defectImages}
                            onRemove={(file) => console.log(file)}
                            onPreview={handlePreview}
                            onChange={({ fileList: newFileList }) => {
                              form.setFieldsValue({
                                performanceTestingStatus: null,
                                testStandStatus: null,
                                faultCategoryId: null,
                                signOfCarbonResidue: null,
                                cutInUnitStatus: null,
                              });
                              setDefectImages(
                                newFileList.map(
                                  (file) =>
                                    ({
                                      ...file,
                                      isUploaded:
                                        (file as any).isUploaded || false,
                                    }) as UploadFile
                                )
                              );
                            }}
                          >
                            <Button icon={<FileAddOutlined />}>
                              {/* Select Images */}
                            </Button>
                          </Upload>
                        </Form.Item>
                        {defectImages.some(
                          (file) => !(file as any).isUploaded
                        ) && (
                          <Button
                            type="primary"
                            onClick={async () =>
                              await handleUploadImages(
                                eEVAL_L1_IMAGES.VISUAL_INSPECTION
                              )
                            }
                            style={{ marginTop: "1rem" }}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Upload Images
                          </Button>
                        )}
                      </Col>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Visual Image Description"
                          name="visualImgDescription"
                          required
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Visual Image Description is required",
                          //   },
                          // ]}
                        >
                          <Input.TextArea
                            rows={4}
                            placeholder="Visual Image Description"
                            allowClear
                          />
                        </Form.Item>
                        {defectLogBookPath ? (
                          <Button
                            disabled={isLoading}
                            type="default"
                            icon={<BookOutlined />}
                            onClick={() =>
                              window.open(defectLogBookPath, "_blank")
                            }
                          >
                            View Defect Code Book
                          </Button>
                        ) : null}
                      </Col>
                    </Row>
                  ) : null}
                </Collapse.Panel>
              ) : null}

              {/* ACCORDION: PERFORMANCE TESTING */}
              {/* areDefectImagesUploaded -- to check if defect images are uploaded  PT. 132 Remove the check */}
              {(stage === eL1_STAGES.PERFORMANCE_TESTING ||
                stage === eL1_STAGES.DISPOSITION) &&
              visualImgDescription ? (
                <Collapse.Panel
                  header={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>
                        {eL1_STAGES_NAME[eL1_STAGES.PERFORMANCE_TESTING]}
                      </span>
                      <div>
                        {faultCategoryId ? (
                          <Tag>
                            {
                              faultCategoryOptions?.find(
                                (category: TDropdownOptions) =>
                                  category.value === faultCategoryId
                              )?.label || "" // fallback if not found
                            }
                          </Tag>
                        ) : null}
                        {performanceTestingStatus ? (
                          <Tag
                            color={
                              performanceTestingStatus ===
                              ePERFORMANCETESTING.FAULT_FOUND
                                ? "error"
                                : "success"
                            }
                          >
                            {performanceTestingStatus ===
                            ePERFORMANCETESTING.FAULT_FOUND
                              ? "Fault Found"
                              : "No Fault Found"}
                          </Tag>
                        ) : null}
                      </div>
                    </div>
                  }
                  key={eL1_STAGES.PERFORMANCE_TESTING}
                >
                  <Row gutter={16}>
                    {noOfStatorUsedId && (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="performanceTestingStatus"
                          label="Performance Testing"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Performance Testing status is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Select Performance Testing"
                            options={Object.entries(ePERFORMANCETESTING)
                              .filter(([key]) => !isNaN(Number(key)))
                              .map(([key, value]) => ({
                                label: (value as string)
                                  .replace(/_/g, " ")
                                  .toLowerCase()
                                  .replace(/^\w/, (c) => c.toUpperCase()),
                                value: Number(key),
                              }))}
                            onChange={async (value) => {
                              handlePerformanceTestingStatusChange(value);
                              await fetchFaultCategoryList();
                            }}
                          />
                        </Form.Item>
                        {performanceTestingStatus ===
                          ePERFORMANCETESTING.NO_FAULT_FOUND && (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginTop: "-20px",
                              color: "var(--bs-success)",
                            }}
                          >
                            <CheckCircleFilled />
                            <span style={{ marginLeft: "8px" }}>
                              {isOlderThanOneYear
                                ? "Unit is older than one year"
                                : "Unit is less than a year old"}
                            </span>
                          </div>
                        )}
                      </Col>
                    )}
                    {/* </Row> */}
                    {/* <Row gutter={16} style={{ marginTop: "1rem" }}> */}
                    {performanceTestingStatus ===
                      ePERFORMANCETESTING.NO_FAULT_FOUND &&
                      !isOlderThanOneYear && (
                        <Col span={8}>
                          <Form.Item
                            name="testStandStatus"
                            label="Test Stand Result"
                            // rules={[
                            //   {
                            //     required: true,
                            //     message: "Test Stand Result is required",
                            //   },
                            // ]}
                          >
                            <Select
                              size="middle"
                              placeholder="Select Test Stand Result"
                              onChange={handleTestStandStatusChange}
                              options={Object.entries(eTESTSTANDRESULT)
                                .filter(([key]) => !isNaN(Number(key)))
                                .map(([key, value]) => ({
                                  label: (value as string)
                                    .replace(/_/g, " ")
                                    .toLowerCase()
                                    .replace(/^\w/, (c) => c.toUpperCase()),
                                  value: Number(key),
                                }))}
                            />
                          </Form.Item>
                        </Col>
                      )}
                    {performanceTestingStatus ===
                      ePERFORMANCETESTING.FAULT_FOUND && (
                      // <Row gutter={16} style={{ marginTop: "1rem" }}>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="faultCategoryId"
                          label="Fault Category"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Fault Category is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Select Fault Category"
                            options={faultCategoryOptions}
                            onChange={handleFaultCategoryIdChange}
                          />
                        </Form.Item>
                      </Col>
                      // </Row>
                    )}
                    {performanceTestingStatus ===
                    ePERFORMANCETESTING.FAULT_FOUND ? (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Additional Notes"
                          name="faultAdditionalNotes"
                        >
                          <Input.TextArea
                            rows={4}
                            placeholder="Additional notes "
                            allowClear
                          />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                </Collapse.Panel>
              ) : null}

              {stage === eL1_STAGES.DISPOSITION && faultCategoryId ? (
                <Collapse.Panel
                  header={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>{eL1_STAGES_NAME[eL1_STAGES.DISPOSITION]}</span>
                      {signOfCarbonResidue && cutInUnitStatus ? (
                        <Tag color="default">
                          {signOfCarbonResidue === eSIGNOFCARBON.YES
                            ? "Sign of Carbon Residue"
                            : "No Sign of Carbon Residue"}
                          {/* Add a space before and after 'and' */}
                          {` and `}
                          {cutInUnitStatus === eVERIFYCUTINUNIT.YES
                            ? "Cut in Unit"
                            : "No Cut in Unit"}
                        </Tag>
                      ) : null}
                    </div>
                  }
                  key="4"
                >
                  <Row gutter={16}>
                    {faultCategoryId && (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="signOfCarbonResidue"
                          label="Sign of Carbon Residue"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Sign of Carbon Residue is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Select Sign of Carbon Residue"
                            onChange={handleSignOfCarbonResidueChange}
                            options={Object.entries(eSIGNOFCARBON)
                              .filter(([key]) => !isNaN(Number(key)))
                              .map(([key, value]) => ({
                                label: (value as string)
                                  .toLowerCase()
                                  .replace(/^\w/, (c) => c.toUpperCase()),
                                value: Number(key),
                              }))}
                          />
                        </Form.Item>
                      </Col>
                    )}
                    {signOfCarbonResidue === eSIGNOFCARBON.YES ? (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="carbonResidueVerification"
                          label="Carbon Residue Verfication"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Cut In Unit is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Open Volute to verify Carbon Residue"
                            onChange={handleVerifyCarbonResidueChange}
                            options={Object.entries(eCARBONRESIDUEVERIFICATION)
                              .filter(([key]) => !isNaN(Number(key)))
                              .map(([key, value]) => ({
                                label: (value as string)
                                  .toLowerCase()
                                  .replace(/^\w/, (c) => c.toUpperCase()),
                                value: Number(key),
                              }))}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}

                    {carbonResidueVerification ||
                    signOfCarbonResidue === eSIGNOFCARBON.NO ? (
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          name="cutInUnitStatus"
                          label="Cut In Unit"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Cut In Unit is required",
                          //   },
                          // ]}
                        >
                          <Select
                            size="middle"
                            placeholder="Select Cut In Unit"
                            onChange={handleCutInUnitStatusChange}
                            options={Object.entries(eVERIFYCUTINUNIT)
                              .filter(([key]) => !isNaN(Number(key)))
                              .map(([key, value]) => ({
                                label: (value as string)
                                  .toLowerCase()
                                  .replace(/^\w/, (c) => c.toUpperCase()),
                                value: Number(key),
                              }))}
                          />
                        </Form.Item>
                      </Col>
                    ) : null}
                  </Row>
                  {carbonResidueVerification ===
                    eCARBONRESIDUEVERIFICATION.YES &&
                  cutInUnitStatus === eVERIFYCUTINUNIT.NO ? (
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Select images of carbon residue"
                          // rules={[{ required: true }]}
                        >
                          <Upload
                            listType="picture-card"
                            multiple
                            showUploadList={{
                              showRemoveIcon: false,
                              showPreviewIcon: true,
                            }}
                            itemRender={(originNode, file) => {
                              const node = React.cloneElement(
                                originNode as React.ReactElement,
                                {
                                  title: "", // removes the tooltip
                                }
                              );
                              return node;
                            }}
                            beforeUpload={(file) => {
                              const isImage = file.type.startsWith("image/");
                              if (!isImage) {
                                message.error(
                                  "You can only upload image files!"
                                );
                              }
                              return isImage || Upload.LIST_IGNORE;
                            }}
                            fileList={carbonResidueImages}
                            onPreview={handlePreview}
                            onChange={({ fileList: newFileList }) => {
                              setCarbonResidueImages(
                                newFileList.map(
                                  (file) =>
                                    ({
                                      ...file,
                                      isUploaded:
                                        (file as any).isUploaded || false,
                                    }) as UploadFile
                                )
                              );
                            }}
                          >
                            <Button icon={<FileAddOutlined />}>
                              {/* Select Images */}
                            </Button>
                          </Upload>
                        </Form.Item>
                        {carbonResidueImages.some(
                          (file) => !(file as any).isUploaded
                        ) && (
                          <Button
                            type="primary"
                            onClick={async () => {
                              await handleUploadImages(
                                eEVAL_L1_IMAGES.CARBON_RESIDUE
                              );
                              addLog(
                                eL1OUTPUTMSGS.READYTOTEARDOWN,
                                "readyForTeardown",
                                eL1_STAGES_NAME[eL1_STAGES.DISPOSITION]
                              );
                            }}
                            style={{ marginTop: "1rem" }}
                            loading={isLoading}
                            disabled={isLoading}
                          >
                            Upload Images
                          </Button>
                        )}
                      </Col>
                      <Col xs={24} sm={12} md={12} lg={12}>
                        <Form.Item
                          label="Carbon Image Description "
                          name="carbonImgDescription"
                          required
                        >
                          <Input.TextArea
                            rows={4}
                            placeholder="Carbon Image Description "
                            allowClear
                          />
                        </Form.Item>
                        {/* {defectLogBookPath ? (
                          <Button
                            disabled={isLoading}
                            type="default"
                            icon={<BookOutlined />}
                            onClick={() =>
                              window.open(defectLogBookPath, "_blank")
                            }
                          >
                            View Defect Code Book
                          </Button>
                        ) : null} */}
                      </Col>
                    </Row>
                  ) : null}
                </Collapse.Panel>
              ) : null}
            </Collapse>

            {/* Next Button After Eval-L1 Scan */}
            {scanResult?.warrantyStatus && stage === eL1_STAGES.EVAL_L1_SCAN ? (
              <Button
                type="primary"
                onClick={async () => {
                  scheduleSubmission();
                  setListRefreshableChanges(true);
                  setStage(eL1_STAGES.HI_POT);
                  setActiveKey(eL1_STAGES.HI_POT);
                }}
                style={{ marginTop: "1rem" }}
              >
                Next
              </Button>
            ) : null}

            {/* Next Button After Hi-Pot */}
            {hiPotStatus === eHIPOT.PASS &&
            noOfStatorUsedId &&
            stage === eL1_STAGES.HI_POT ? (
              <Button
                type="primary"
                onClick={() => {
                  setStage(eL1_STAGES.VISUAL_INSPECTION);
                  setActiveKey(eL1_STAGES.VISUAL_INSPECTION);
                  onFinish(form.getFieldsValue()); // FOR API CALL WHEN NEXT IS CLICKED FOR VISUAL IMAGE DESCRIPTION INPUT FIELD
                }}
                style={{ marginTop: "1rem" }}
              >
                Next
              </Button>
            ) : null}

            {/* Next Button After Visual Inspection */}
            {visualImgDescription?.length > 0 &&
            stage === eL1_STAGES.VISUAL_INSPECTION ? (
              <Button
                type="primary"
                onClick={() => {
                  setStage(eL1_STAGES.PERFORMANCE_TESTING);
                  setActiveKey(eL1_STAGES.PERFORMANCE_TESTING);
                  onFinish(form.getFieldsValue()); // FOR API CALL WHEN NEXT IS CLICKED FOR VISUAL IMAGE DESCRIPTION INPUT FIELD
                }}
                style={{ marginTop: "1rem" }}
              >
                Next
              </Button>
            ) : null}

            {/* Next Button AFter Disposition */}
            {performanceTestingStatus === ePERFORMANCETESTING.FAULT_FOUND &&
            faultCategoryId &&
            stage === eL1_STAGES.PERFORMANCE_TESTING ? (
              <Button
                type="primary"
                onClick={() => {
                  setStage(eL1_STAGES.DISPOSITION);
                  setActiveKey(eL1_STAGES.DISPOSITION);
                  onFinish(form.getFieldsValue());
                }}
                style={{ marginTop: "1rem" }}
              >
                Next
              </Button>
            ) : null}
            {/* {faultCategoryId && (
              <Button
                type="primary"
                onClick={() => {
                  setStage(eL1_STAGES.DISPOSITION);
                  setActiveKey(eL1_STAGES.DISPOSITION);
                }}
                style={{ marginTop: "1rem" }}
              >
                Next 5
              </Button>
            )} */}
            {(rustInConnectorStatus === eRUSTINCONNECTOR.NO ||
              (rustInConnectorStatus === eRUSTINCONNECTOR.YES &&
                areRustImagesUploaded &&
                rustImgDescription) ||
              testStandStatus ||
              (performanceTestingStatus ===
                ePERFORMANCETESTING.NO_FAULT_FOUND &&
                isOlderThanOneYear) ||
              (cutInUnitStatus === eVERIFYCUTINUNIT.NO &&
                carbonResidueVerification === eCARBONRESIDUEVERIFICATION.NO) ||
              (cutInUnitStatus === eVERIFYCUTINUNIT.NO &&
                cutInUnitStatus === eVERIFYCUTINUNIT.NO) ||
              (cutInUnitStatus &&
                signOfCarbonResidue &&
                (cutInUnitStatus === eVERIFYCUTINUNIT.YES ||
                  (cutInUnitStatus === eVERIFYCUTINUNIT.NO &&
                    areCarbonResidueImagesUploaded)))) &&
              submittionStatus !== eEVAL_L1_WORKFLOW.SUBMITTED && (
                <Button
                  type="primary"
                  onClick={handleFormSubmit}
                  disabled={isLoading}
                  loading={isLoading}
                  size="middle"
                  style={{ marginTop: "1rem" }}
                >
                  Submit
                </Button>
              )}
          </Form>
        </FormLayout>
      </Col>
      {showLogs && (
        <Col
          xs={24}
          sm={24}
          md={6}
          lg={6}
          xl={6}
          className={styles.rightContainer}
        >
          <h3
            style={{
              textAlign: "center",
              margin: 0,
              fontWeight: 500,
              color: "rgba(0, 0, 0, 0.85)",
              padding: "0 1rem",
            }}
          >
            Output Messages History
          </h3>
          {logs?.length === 0 ? (
            <Empty
              description="No output messages hitory to display"
              style={{ marginTop: "2rem" }}
            />
          ) : (
            <div style={{ marginTop: "1rem" }}>
              {logs?.map((log, index) =>
                log?.scanResult ? (
                  <ScanResultCard
                    key={index}
                    timeStamp={log.timeStamp}
                    scanResult={log.scanResult}
                    outputStyle={outputStyle}
                    context={context}
                  />
                ) : (
                  <InfoDisplayLog
                    key={index}
                    message={log.message}
                    timestamp={log.timeStamp}
                    stageName={log.stageName}
                    context={context}
                  />
                )
              )}
            </div>
          )}
        </Col>
      )}
    </Row>
  );
}
